import { useEffect, useMemo, useRef } from "react";
import styled from "styled-components";
import { Modal } from "@/components/modal/modal";
import { Button } from "@/components/ui/button";
import { openSupportChat } from "libs/support";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Icon } from "@dialogueconsulting/sked-ui";
import { openHowdyGoDemoDialog, closeHowdyGoDemoDialog } from "./model";

const HOWDYGO_SCRIPT_SRC = "https://js.howdygo.com/v1.2.1/index.js";
const HOWDYGO_SCRIPT_ID = "howdygo-embed-script";
const HOWDYGO_SCRIPT_INTEGRITY =
  typeof process !== "undefined" && process.env
    ? process.env.REACT_APP_HOWDYGO_SRI
    : undefined;

const HOWDYGO_EMBED_URLS = {
  calendar:
    "https://app.howdygo.com/embed/51656815-4bcf-4773-a2ed-e2f03c654310",
  inbox: "https://app.howdygo.com/embed/4466159c-9908-4921-a1c1-c623235dc5ad",
  insights:
    "https://app.howdygo.com/embed/2b441474-dc91-450f-9dd9-182d4a7b3a31",
  customReports:
    "https://app.howdygo.com/embed/e6417cef-04fb-46b1-a271-e917b3d809ba",
  socialListening:
    "https://app.howdygo.com/embed/945943e9-3819-4de6-b758-b7d88c4b2a3c",
  labels: "https://app.howdygo.com/embed/15c8f9a8-6907-4278-90e9-ccdd814b5f50",
  planning:
    "https://app.howdygo.com/embed/28cc9271-9034-4536-aeae-f49a602db95c",
  // URL parameter-triggered tours
  publishing:
    "https://app.howdygo.com/embed/9a836e29-0452-48b6-9714-257950c98356",
  teamwork:
    "https://app.howdygo.com/embed/0dd4f3ab-77fe-4618-a5cc-e2e831c78648",
  competitors:
    "https://app.howdygo.com/embed/69caf8eb-402e-4d54-825e-bf37a8acf916",
  listening:
    "https://app.howdygo.com/embed/77f7c21b-1b47-464d-b5ee-a56d51a055ef",
} as const;

export const resolveHowdyGoContext = (
  pathname: string
): { url: string; label: string } | null => {
  const normalized = pathname.toLowerCase();

  if (
    normalized.startsWith("/dashboard/social-listening") ||
    normalized.startsWith("/dashboard/insights-v2/social-listening")
  ) {
    return {
      url: HOWDYGO_EMBED_URLS.socialListening,
      label: "Social Listening",
    };
  }
  if (
    normalized.startsWith("/dashboard/insights/report-builder") ||
    normalized.startsWith("/dashboard/insights-v2/report-builder")
  ) {
    return {
      url: HOWDYGO_EMBED_URLS.customReports,
      label: "Custom Reports",
    };
  }
  if (
    normalized.startsWith("/dashboard/insights/labels") ||
    normalized.startsWith("/dashboard/insights-v2/labels") ||
    normalized.startsWith("/dashboard/workshop/labels")
  ) {
    return { url: HOWDYGO_EMBED_URLS.labels, label: "Labels" };
  }
  if (
    normalized.startsWith("/dashboard/insights") ||
    normalized.startsWith("/dashboard/insights-v2")
  ) {
    return {
      url: HOWDYGO_EMBED_URLS.insights,
      label: "Insights",
    };
  }
  if (normalized.startsWith("/dashboard/inbox")) {
    return { url: HOWDYGO_EMBED_URLS.inbox, label: "Inbox" };
  }
  if (normalized.startsWith("/dashboard/calendar")) {
    return { url: HOWDYGO_EMBED_URLS.calendar, label: "Calendar" };
  }
  if (normalized.startsWith("/dashboard/idea-planner")) {
    return { url: HOWDYGO_EMBED_URLS.planning, label: "Ideas" };
  }

  return null;
};

// Valid tour IDs that can be triggered via URL parameter
export const VALID_TOUR_IDS = [
  "publishing",
  "teamwork",
  "competitors",
  "inbox",
  "insights",
  "listening",
  "labels",
  "planning",
] as const;

export type TourId = (typeof VALID_TOUR_IDS)[number];

export const isValidTourId = (tourId: string | null): tourId is TourId => {
  return tourId !== null && VALID_TOUR_IDS.includes(tourId as TourId);
};

// Tour configuration mapping for URL parameter-triggered tours
const TOUR_CONFIGS: Record<
  string,
  {
    embedId: string;
    label: string;
    paddingBottom: string;
    maxWidth: string;
  }
> = {
  publishing: {
    embedId: "9a836e29-0452-48b6-9714-257950c98356",
    label: "Automation & Publishing",
    paddingBottom: "calc(58.1045751633987% + 40px)",
    maxWidth: "1530px",
  },
  teamwork: {
    embedId: "0dd4f3ab-77fe-4618-a5cc-e2e831c78648",
    label: "Collaboration & Teamwork",
    paddingBottom: "calc(58.64285714285714% + 40px)",
    maxWidth: "1400px",
  },
  competitors: {
    embedId: "69caf8eb-402e-4d54-825e-bf37a8acf916",
    label: "Competitive Intelligence",
    paddingBottom: "calc(60% + 40px)",
    maxWidth: "1500px",
  },
  inbox: {
    embedId: "05953cdb-066d-4a86-ab7c-3d870717a024",
    label: "Engagement & Support",
    paddingBottom: "calc(60% + 40px)",
    maxWidth: "1500px",
  },
  insights: {
    embedId: "074f70b3-3176-4283-9150-c3de3e0a934b",
    label: "Insight Reporting",
    paddingBottom: "calc(64.28571428571429% + 40px)",
    maxWidth: "1400px",
  },
  listening: {
    embedId: "77f7c21b-1b47-464d-b5ee-a56d51a055ef",
    label: "Insights & Monitoring",
    paddingBottom: "calc(64.28571428571429% + 40px)",
    maxWidth: "1400px",
  },
  labels: {
    embedId: "6f030dc2-60de-45f4-94ec-f7a3904e7b9f",
    label: "Labels & Campaigns",
    paddingBottom: "calc(64.28571428571429% + 40px)",
    maxWidth: "1400px",
  },
  planning: {
    embedId: "28cc9271-9034-4536-aeae-f49a602db95c",
    label: "Planning & Visibility",
    paddingBottom: "calc(64.28571428571429% + 40px)",
    maxWidth: "1400px",
  },
};

type HowdyGoDemoModalProps =
  | {
      // Demo mode: auto-shows when no accounts exist
      mode: "demo";
      show: boolean;
      existingAccountsCount: number;
      accountsReady: boolean;
      onLaunchAddAccount: () => void;
      isMainDialogGuarded: boolean;
    }
  | {
      // Tour mode: triggered by URL parameter
      mode: "tour";
      show: boolean;
      tourId: string;
      onHide: () => void;
    };

export function HowdyGoDemoModal(props: HowdyGoDemoModalProps) {
  const { show, mode } = props;
  const location = useLocation();
  const hasAutoOpenedRef = useRef(false);
  const scriptRef = useRef<HTMLScriptElement | null>(null);

  // Determine if this is tour mode (URL parameter) or demo mode (no accounts)
  const isTourMode = mode === "tour";
  const tourId = isTourMode ? props.tourId : null;
  const tourConfig =
    tourId && TOUR_CONFIGS[tourId] ? TOUR_CONFIGS[tourId] : undefined;

  // Extract demo mode props when needed
  const existingAccountsCount =
    mode === "demo" ? props.existingAccountsCount : 0;
  const accountsReady = mode === "demo" ? props.accountsReady : true;
  const onLaunchAddAccount =
    mode === "demo" ? props.onLaunchAddAccount : undefined;
  const isMainDialogGuarded =
    mode === "demo" ? props.isMainDialogGuarded : false;
  const onHide = mode === "tour" ? props.onHide : undefined;

  const howdyGoContext = useMemo(
    () => (isTourMode ? null : resolveHowdyGoContext(location.pathname)),
    [location.pathname, isTourMode]
  );

  // The context to use: either tour config or path-based demo context
  const context =
    isTourMode && tourConfig
      ? {
          url: `https://app.howdygo.com/embed/${tourConfig.embedId}`,
          label: tourConfig.label,
        }
      : howdyGoContext;

  useEffect(() => {
    // In tour mode, don't require accountsReady
    if (!context || (!isTourMode && !accountsReady)) {
      return;
    }

    const className = "howdygo-modal-open";
    if (show) {
      document.body.classList.add(className);
    } else {
      document.body.classList.remove(className);
    }

    return () => {
      document.body.classList.remove(className);
    };
  }, [show, context, accountsReady, isTourMode]);

  useEffect(() => {
    // In tour mode, don't require accountsReady
    if (!show || !context || (!isTourMode && !accountsReady)) {
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>(
      `#${HOWDYGO_SCRIPT_ID}`
    );
    if (existingScript) {
      scriptRef.current = existingScript;
      return;
    }

    const script = document.createElement("script");
    script.id = HOWDYGO_SCRIPT_ID;
    script.src = HOWDYGO_SCRIPT_SRC;
    script.async = true;
    script.crossOrigin = "anonymous";
    if (HOWDYGO_SCRIPT_INTEGRITY) {
      script.integrity = HOWDYGO_SCRIPT_INTEGRITY;
    }

    const handleLoad = () => {
      scriptRef.current = script;
    };

    const handleError = () => {
      scriptRef.current = null;
      // Only remove if still in DOM
      if (script.parentNode) {
        script.remove();
      }
      toast.error(
        "We couldn't load the HowdyGo demo. Please try again or contact support."
      );
    };

    script.addEventListener("load", handleLoad);
    script.addEventListener("error", handleError);

    document.body.append(script);
    scriptRef.current = script;

    return () => {
      // Remove event listeners to prevent memory leaks
      script.removeEventListener("load", handleLoad);
      script.removeEventListener("error", handleError);

      // Only remove script if it was successfully loaded and is still in DOM
      if (scriptRef.current === script && script.parentNode) {
        scriptRef.current.remove();
        scriptRef.current = null;
      }
    };
  }, [show, context, accountsReady, isTourMode]);

  // Reset auto-open tracking when navigating to different routes/contexts (only for demo mode)
  useEffect(() => {
    // Don't reset if the modal is currently showing to prevent race conditions
    if (!isTourMode && !show) {
      hasAutoOpenedRef.current = false;
    }
  }, [howdyGoContext, show, isTourMode]);

  // Auto-open logic (only for demo mode, not tour mode)
  useEffect(() => {
    if (
      !isTourMode &&
      accountsReady &&
      !hasAutoOpenedRef.current &&
      existingAccountsCount === 0 &&
      !show &&
      howdyGoContext &&
      !isMainDialogGuarded
    ) {
      openHowdyGoDemoDialog();
      hasAutoOpenedRef.current = true;
    }
  }, [
    accountsReady,
    existingAccountsCount,
    show,
    howdyGoContext,
    isTourMode,
    isMainDialogGuarded,
  ]);

  useEffect(() => {
    if (show) {
      hasAutoOpenedRef.current = true;
    }
  }, [show]);

  // Auto-close logic (only for demo mode, not tour mode)
  useEffect(() => {
    if (!isTourMode && show && (!howdyGoContext || !accountsReady)) {
      closeHowdyGoDemoDialog();
    }
  }, [show, howdyGoContext, accountsReady, isTourMode]);

  // In tour mode, don't require accountsReady
  if (!show || !context || (!isTourMode && !accountsReady)) {
    return null;
  }

  const isPlannerContext = !isTourMode && context?.label === "Ideas";

  // eslint-disable-next-line unicorn/consistent-function-scoping
  const handleSupportClick = () => openSupportChat("", "accounts");

  return (
    <HowdyGoModal
      show={show}
      onHide={isTourMode ? onHide || (() => {}) : () => {}}
      backdrop="static"
      keyboard={isTourMode}
      modal={false}
      className={isTourMode ? "howdygo-tour-modal" : "howdygo-demo-modal"}
      dialogClassName="sked-modal-dialog"
      maxWidth={
        tourConfig?.maxWidth || (isPlannerContext ? "920px" : undefined)
      }
      $variant={isPlannerContext ? "planner" : "default"}
      scopeToContent
    >
      {isTourMode && (
        <Modal.Header closeButton>
          <Modal.Title>{context.label}</Modal.Title>
        </Modal.Header>
      )}
      <Modal.Body>
        <Content>
          {!isTourMode && (
            <HeaderRow>
              <Title $variant={isPlannerContext ? "planner" : "default"}>
                Connect a social account to see{" "}
                <Highlight $variant={isPlannerContext ? "planner" : "default"}>
                  {context.label}
                </Highlight>{" "}
                in action.
              </Title>
              <Button
                variant="blue-primary"
                size={isPlannerContext ? "sm" : "md"}
                onClick={onLaunchAddAccount}
                data-testid="howdyGoLaunchAddAccountButton"
              >
                <i className="fa-regular fa-plus" />
                Add your first account
              </Button>
            </HeaderRow>
          )}

          <EmbedContainer id="howdygo-embed">
            <IframeWrapper paddingBottom={tourConfig?.paddingBottom}>
              <iframe
                id="howdygo-frame"
                src={context.url}
                allow="clipboard-write; autoplay"
                sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                allowFullScreen
                title={`${context.label} ${isTourMode ? "tour" : "interactive demo"}`}
              />
            </IframeWrapper>
          </EmbedContainer>

          {!isTourMode && (
            <SupportText $variant={isPlannerContext ? "planner" : "default"}>
              {isPlannerContext
                ? "Any questions? "
                : `Any questions about ${context.label}? `}
              <SupportLink onClick={handleSupportClick}>
                Contact Support
              </SupportLink>
              {isPlannerContext && (
                <SupportIcon>
                  <Icon name="heart-hand" size="sm" color="#4f31ff" />
                </SupportIcon>
              )}
            </SupportText>
          )}
        </Content>
      </Modal.Body>
    </HowdyGoModal>
  );
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const HeaderRow = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const Title = styled.h2<{ $variant?: "planner" | "default" }>`
  margin: 0;
  color: #0e1680;
  font-size: ${(props) => (props.$variant === "planner" ? "24px" : "28px")};
  font-weight: ${(props) => (props.$variant === "planner" ? 700 : 600)};
  line-height: ${(props) => (props.$variant === "planner" ? "32px" : "1.3")};
  font-family: ${(props) =>
    props.$variant === "planner"
      ? "'Gotham SSm A', 'Gotham SSm B', Helvetica, Arial, sans-serif"
      : "inherit"};
`;

const Highlight = styled.span<{ $variant?: "planner" | "default" }>`
  ${(props) =>
    props.$variant === "planner"
      ? `
        color: #4f31ff;
      `
      : `
        background: linear-gradient(90deg, #4f31ff 0%, #8b5cf6 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      `}
`;

const EmbedContainer = styled.div`
  width: 100%;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow:
    0px 0px 1px rgba(45, 55, 72, 0.05),
    0px 4px 8px rgba(45, 55, 72, 0.1);
`;

const IframeWrapper = styled.div<{ paddingBottom?: string }>`
  position: relative;
  width: 100%;
  padding-bottom: ${(props) =>
    props.paddingBottom || "calc(58.1045751633987% + 40px)"};

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const SupportText = styled.p<{ $variant?: "planner" | "default" }>`
  margin: 0;
  color: #454545;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const SupportLink = styled.button.attrs({ type: "button" })`
  background: none;
  border: none;
  padding: 0;
  color: #4f31ff;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const SupportIcon = styled.span`
  display: inline-flex;
  align-items: center;
`;

const HowdyGoModal = styled(Modal)<{
  maxWidth?: string;
  $variant?: "planner" | "default";
  scopeToContent?: boolean;
}>`
  margin: ${(props) => (props.maxWidth ? "60px" : "120px")} auto 0;

  .modal-dialog {
    max-width: ${(props) => props.maxWidth || "1200px"};
    width: min(90vw, ${(props) => props.maxWidth || "1200px"});
  }

  .modal-content {
    border-radius: ${(props) =>
      props.$variant === "planner" ? "8px" : "16px"};
  }

  .modal-body {
    padding: 0;
  }
`;
